<template>
  <section
    :class="[
      'articledata',
      {
        'is-wrapper': wrapper,
        'is-expandable': expandable,
        'is-expanded': expanded
      }
    ]"
    :aria-expanded="expandable ? expanded ? 'true' : 'false' : null"
  >
    <b-row>
      <b-col :[$root.mobile.up.key]="3">
        <b-badge pill class="articledata-title" :variant="titleVariant">
          <slot name="title"/>
        </b-badge>
      </b-col>

      <b-col :[$root.mobile.up.key]="9">
        <transition-expand v-if="expandable" :minHeight="minHeight">
          <div class="articledata-content" :key="expanded">
            <slot/>
          </div>
        </transition-expand>

        <div v-else class="articledata-content">
          <slot/>
        </div>
      </b-col>
    </b-row>

    <a v-if="expandable" href="#" class="articledata-expander" @click.prevent="toggleExpanded"><b-icon icon="plus" scale="1.8"/></a>
  </section>
</template>

<script>
import SCSS_VARIABLES from '@/assets/js/modules/scss-variables'

const VARIANTS = [null, ''].concat(Object.keys(SCSS_VARIABLES.colors.theme))

export default {
  name: 'ArticleData',
  props: {
    wrapper: {
      type: Boolean,
      default: false
    },
    expandable: {
      type: Boolean,
      default: null
    },
    titleVariant: {
      type: String,
      default: null,
      validator: value => VARIANTS.includes(value)
    }
  },
  data () {
    return {
      minHeight: 100,
      expanded: this.expandable ? false : null
    }
  },
  methods: {
    toggleExpanded () {
      this.expanded = !this.expanded
    }
  }
}
</script>

<style lang="scss">
$articledata-gap: $component-gap * 0.5 !default;
$articledata-padding-y: $articledata-gap !default;
$articledata-padding-x: 0 !default;
$articledata-border: $border-width $border-style $border-color !default;
$articledata-font-size: $font-size-sm !default;

$articledata-title-gap: 0 !default;
$articledata-title-font-size: inherit !default;

$articledata-item-gap: $articledata-font-size !default;
$articledata-item-title-gap: $articledata-gap !default;
$articledata-item-separated-border: $articledata-border !default;

$articledata-content-box-shadow-spread: $spacer !default;
$articledata-content-box-shadow: inset 0 ($articledata-content-box-shadow-spread * -0.75) $articledata-content-box-shadow-spread ($articledata-content-box-shadow-spread * 0.25) $white !default;
$articledata-content-transition-in: all ease-out 400ms !default;
$articledata-content-transition-out: all ease-out 300ms !default;

$articledata-expander-gap: $articledata-title-gap * 0.5 !default;
$articledata-expander-transition-in: transform cubic-bezier(0.35, 0.05, 0.4, 1.5) 500ms !default;
$articledata-expander-transition-out: transform cubic-bezier(0.35, 0.05, 0.4, 1.5) 200ms !default;

$articledata-mobile-breakpoint: $mobile-breakpoint !default;

$articledata-mobile-title-gap: $spacer !default;

.articledata {
  position: relative;
  margin-top: $articledata-gap;
  margin-bottom: $articledata-gap;
  padding: $articledata-padding-y $articledata-padding-x 0;
  border-top: $articledata-border;
  font-size: $articledata-font-size;

  .articledata-title {
    margin-bottom: $articledata-title-gap;
    font-size: $articledata-title-font-size;
    text-align: left;
    white-space: normal;

    > * {
      margin: 0;
      padding: 0;
      width: auto;
      height: auto;
      font-size: inherit;
      font-weight: inherit;
      font-style: inherit;
      color: inherit;
      line-height: inherit;
    }
  }

  &:not(.is-wrapper) {
    .articledata-content {
      dl {
        display: flex;
        flex-wrap: wrap;
        width: 100%;

        > dt,
        > dd {
          flex: 0 0 50%;
          max-width: 50%;
          margin: 0;
          padding-top: $articledata-item-gap;
          text-overflow: ellipsis;
          overflow: hidden;

          .subject-information{
            margin-bottom:10px;
            &:last-child{
              margin-bottom:0;
            }
          }
        }

        &.separated {
          > dt,
          > dd {
            padding-bottom: $articledata-item-gap;
            border-bottom: $articledata-item-separated-border;
          }

          > dt:nth-last-child(2) {
            padding-bottom: 0;
            border-bottom: 0;

            + dd {
              padding-bottom: 0;
              border-bottom: 0;
            }
          }
        }

        > dt:first-child {
          padding-top: 0;

          + dd {
            padding-top: 0;
          }
        }

        > dt{
          padding-right: $articledata-item-gap;
        }
      }

      table {
        table-layout: fixed;
        width: 100%;

        thead,
        tbody,
        tfoot {
          > tr {
            > th,
            > td {
              padding-top: $articledata-item-gap;
            }
          }

          &:first-child {
            > tr {
              &:first-child {
                > th,
                > td {
                  padding-top: 0;
                }
              }
            }
          }
        }

        thead > tr,
        tbody > tr.thead-tr,
        tfoot > tr.thead-tr {
          > th {
            padding-top: $articledata-item-title-gap;
            padding-bottom: $articledata-item-gap;
            border-bottom: $articledata-item-separated-border;
          }
        }
      }

      > *:last-child {
        margin-bottom: 0;
      }

      &.expand-enter-active {
        transition: $articledata-content-transition-in;
      }

      &.expand-leave-active {
        transition: $articledata-content-transition-out;
      }
    }

    .articledata-expander {
      display: block;
      float: right;
      margin-top: $articledata-expander-gap;
      transition: $articledata-expander-transition-out;
    }

    &.is-expandable {
      .articledata-content {
        position: relative;
        overflow: hidden;

        &:before {
          display: block;
          content: '';
          position: absolute;
          bottom: 0;
          left: $articledata-content-box-shadow-spread * -2;
          right: $articledata-content-box-shadow-spread * -2;
          height: 200%;
          box-shadow: $articledata-content-box-shadow;
          transition: inherit;
        }

        &.expand-enter,
        &.expand-leave-to {
          &:before {
            transform: translateY($articledata-content-box-shadow-spread * 0);
          }
        }

        &.expand-enter-to,
        &.expand-leave {
          &:before {
            transform: translateY($articledata-content-box-shadow-spread * 2);
          }
        }

        &.expand-state-open {
          &:before {
            display: none;
          }
        }
      }

      &.is-expanded {
        .articledata-expander {
          transform: rotate(-135deg);
          transition: $articledata-expander-transition-in;
        }
      }
    }
  }

  .form-control, .custom-select, .custom-control, .b-custom-control { font-size: $input-font-size; }
  .form-control-sm, .custom-select-sm, .custom-control-sm, .b-custom-control-sm { font-size: $input-font-size-sm; }
  .form-control-lg, .custom-select-lg, .custom-control-lg, .b-custom-control-lg { font-size: $input-font-size-lg; }

  @include media-breakpoint-down($articledata-mobile-breakpoint) {
    .articledata-title {
      margin-bottom: $articledata-mobile-title-gap;
    }

    &:not(.is-wrapper){
      .articledata-content {
        dl {
          display: block;

          > dt,
          > dd {
            max-width: none;
          }

          > dd {
            padding-top: 0;
          }

          &.separated {
            > dt {
              padding-bottom: 0;
              border-bottom: 0 none;
            }
          }
        }

        table {
          display: block;

          > thead,
          > tfoot {
            display: none;
          }

          > tbody {
            display: block;

            > tr {
              display: block;

              > th,
              > td {
                display: block;
              }

              > th {
                ~ td {
                  padding-top: 0;

                  &[data-th] {
                    &:before {
                      display: block;
                      content: attr(data-th);
                      padding-top: $articledata-item-gap;
                      font-weight: $table-th-font-weight;
                    }

                    &:last-child {
                      margin-bottom: calc(#{$articledata-item-title-gap} - #{$articledata-item-gap});
                    }
                  }
                }

                + td {
                  &[data-th] {
                    &:before {
                      margin-top: $articledata-item-gap;
                      border-top: $articledata-item-separated-border;
                    }
                  }
                }
              }

              &:first-child {
                > th,
                > td {
                  &:first-child {
                    padding-top: 0;
                  }
                }
              }

              &:last-child {
                > th {
                  ~ td {
                    &[data-th] {
                      &:last-child {
                        margin-bottom: 0;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
